import { Container } from '@acies/core';
import { IconTitle, LabelValueUnit, Spacer, Spinner, StrokeContainer } from '@acies/ui';
import type { IBom } from '../../../domain/calculations';
import { ConfiguratorModel } from '../configurator.model';
import PositionErrorMessage from '../shared/position-error-message/position-error-message';

/**
 * What the engine worked out about the position being worked on, in a panel of its own at the right-hand edge.
 *
 * One panel, shown or hidden as a whole — see {@link PropertiesPanel.shownChanged} — rather than an open and a closed side with the button that swaps them: the button sits in the top bar now, so the panel has nothing to show while hidden.
 */
export default class PropertiesPanel extends StrokeContainer {
	/**
	 * What the engine worked out about the position being worked on, or null when there is nothing worked out to show.
	 *
	 * Null is a panel with its header and nothing under it, rather than a panel of empty fields: a field with no value in it says the engine worked something out and it was nothing, which is not what has happened.
	 *
	 * @param {IBom | null} value The BOM to show, or null to show none.
	 */
	public set properties(value: IBom | null) {
		if (this.#properties === value) {
			return;
		}
		this.#properties = value;
		this.#propertiesChanged = true;
		this.invalidateProperties();
	}

	public get properties(): IBom | null {
		return this.#properties;
	}

	#properties: IBom | null;

	#propertiesChanged: boolean;
	#header!: IconTitle;
	#body!: Container;
	#content!: Container;
	#spinner!: Spinner;
	#modelWeight!: LabelValueUnit;
	#glassWeight!: LabelValueUnit;
	#topSpacer!: Spacer;
	#bottomSpacer!: Spacer;
	#uValue!: LabelValueUnit;
	#eref!: LabelValueUnit;
	#sunlightTransmittance!: LabelValueUnit;
	#sound!: LabelValueUnit;
	#costPrice!: LabelValueUnit;
	#listPrice!: LabelValueUnit;
	#costPriceElements!: LabelValueUnit;
	#listPriceElements!: LabelValueUnit;
	#costPriceGlasses!: LabelValueUnit;
	#listPriceGlasses!: LabelValueUnit;
	#costPriceCoating!: LabelValueUnit;
	#listPriceCoating!: LabelValueUnit;
	#positionErrorMessage!: PositionErrorMessage;
	public constructor() {
		super();
		this.#properties = null;
		this.#propertiesChanged = false;
		// the listener is registered on the model, so it would otherwise run in its scope rather
		// than in this one
		this.propertiesChanged = this.propertiesChanged.bind(this);
		// Subscribed here rather than on connect, because an Observable tells its listeners about a
		// change and not about the value it already holds: a panel that subscribed later would miss
		// the position that had already been selected. Subscribed while hidden as well, so the panel
		// is never shown with the properties of a position the user has left.
		ConfiguratorModel.currentProperties.subscribe(this.propertiesChanged);
		// and whether the panel is on screen at all, which the show/hide button in the top bar flips
		// through the model — the panel never sees the button
		this.shownChanged = this.shownChanged.bind(this);
		ConfiguratorModel.propertiesPanelShown.subscribe(this.shownChanged);
		// and whether a price is on its way with nothing to show meanwhile; subscribed in initialize,
		// for the reason given there
		this.waitingChanged = this.waitingChanged.bind(this);
		// the 416 the properties wrap in, and the frame's 16 of padding either side of them
		this.width = 400;
		this.height = 'fill';
		this.autoLayout = 'vertical';
		this.gapVertical = 12;
	}

	/** The title across the top of the panel, which says what the panel is. The button that showed and hid the panel used to sit beside it, and sits in the top bar now. */
	public get header(): IconTitle {
		if (!this.#header) {
			this.#header = new IconTitle();
			this.#header.icon = 'atom-01';
			this.#header.text = 'Egenskaber';
		}
		return this.#header;
	}

	/**
	 * The room under the header, which the properties, the error card and the spinner share.
	 *
	 * Centred, for the spinner: the properties fill the room whatever its alignment, and the spinner sits in the middle of the room they leave while they are hidden — the way the drawing section and the geometry dialog centre theirs.
	 */
	public get body(): Container {
		if (!this.#body) {
			this.#body = new Container();
			this.#body.width = 'fill';
			this.#body.height = 'fill';
			this.#body.autoLayout = 'vertical';
			this.#body.alignment = 'center';
			this.#body.gapVertical = 12;
		}
		return this.#body;
	}

	/**
	 * Says that a price is on its way, in the middle of the room the properties will take.
	 *
	 * Shown only while a configuration or a calculation is out for the selected position and there are no properties to show — see {@link PropertiesPanel.waitingChanged}. Properties already on screen are left there while another price is worked out, since a stale price says more than a spinner.
	 */
	public get spinner(): Spinner {
		if (!this.#spinner) {
			this.#spinner = new Spinner();
			this.#spinner.visible = false;
		}
		return this.#spinner;
	}

	/**
	 * The properties themselves, which wrap into as many rows as they need, and scroll when the rows need more height than the panel has left for them.
	 *
	 * Clipped, and not only for the scrollbar: a flex child that is not clipped cannot be made lower than its content, so the rows would grow past the room the panel gives them and the error card under them would be drawn over the last of them. Clipped, the rows take the room there is and the card keeps its place
	 * below.
	 */
	public get content(): Container {
		if (!this.#content) {
			this.#content = new Container();
			this.#content.width = 'fill';
			this.#content.height = 'fill';
			this.#content.autoLayout = 'wrap';
			this.#content.clipContent = true;
			// the scrollbar is there from the first row rather than appearing at the one that overflows,
			// so the rows never shift sideways when it does
			this.#content.alwaysShowVerticalScrollbar = true;
			// room between the last column and the scrollbar, so a value does not run up against it
			this.#content.paddingRight = 16;
			// nothing is selected when the panel is built, so there is nothing worked out to show
			this.#content.visible = false;
			this.#content.gapHorizontal = 16;
			this.#content.gapVertical = 16;
			this.#content.addComponents([
				this.modelWeight,
				this.glassWeight,
				this.topSpacer,
				this.uValue,
				this.eref,
				this.sunlightTransmittance,
				this.sound,
				this.bottomSpacer,
				this.costPrice,
				this.listPrice,
				this.costPriceElements,
				this.listPriceElements,
				this.costPriceGlasses,
				this.listPriceGlasses,
				this.costPriceCoating,
				this.listPriceCoating,
			]);
		}
		return this.#content;
	}

	/** What the model weighs. */
	public get modelWeight(): LabelValueUnit {
		if (!this.#modelWeight) {
			this.#modelWeight = new LabelValueUnit();
			this.#modelWeight.minWidth = 184;
			this.#modelWeight.width = 'fill';
			this.#modelWeight.text = 'Modelvægt';
			this.#modelWeight.unit = 'Kg';
		}
		return this.#modelWeight;
	}

	/** What the glass in the model weighs, of the weight above. */
	public get glassWeight(): LabelValueUnit {
		if (!this.#glassWeight) {
			this.#glassWeight = new LabelValueUnit();
			this.#glassWeight.minWidth = 184;
			this.#glassWeight.width = 'fill';
			this.#glassWeight.text = 'Glasvægt';
			this.#glassWeight.unit = 'Kg';
		}
		return this.#glassWeight;
	}

	/** Fills the rest of its row, so the measurements below it start on one of their own. */
	public get topSpacer(): Spacer {
		if (!this.#topSpacer) {
			this.#topSpacer = new Spacer(24, 'fill', 0, 368);
		}
		return this.#topSpacer;
	}

	/** The same again, so the prices below it are a section of their own. */
	public get bottomSpacer(): Spacer {
		if (!this.#bottomSpacer) {
			this.#bottomSpacer = new Spacer(24, 'fill', 0, 368);
		}
		return this.#bottomSpacer;
	}

	/** How much heat the model lets through. */
	public get uValue(): LabelValueUnit {
		if (!this.#uValue) {
			this.#uValue = new LabelValueUnit();
			this.#uValue.minWidth = 184;
			this.#uValue.width = 'fill';
			this.#uValue.text = 'U-værdi';
			this.#uValue.unit = 'W/m²K';
		}
		return this.#uValue;
	}

	/** What the model gains against what it loses, measured as the reference window. */
	public get eref(): LabelValueUnit {
		if (!this.#eref) {
			this.#eref = new LabelValueUnit();
			this.#eref.minWidth = 184;
			this.#eref.width = 'fill';
			this.#eref.text = 'Eref';
			this.#eref.unit = 'EW';
		}
		return this.#eref;
	}

	/** How much daylight the model lets through. */
	public get sunlightTransmittance(): LabelValueUnit {
		if (!this.#sunlightTransmittance) {
			this.#sunlightTransmittance = new LabelValueUnit();
			this.#sunlightTransmittance.minWidth = 184;
			this.#sunlightTransmittance.width = 'fill';
			this.#sunlightTransmittance.text = 'Sollystransmittans';
			this.#sunlightTransmittance.unit = 'LT';
		}
		return this.#sunlightTransmittance;
	}

	/** How much sound the model keeps out. */
	public get sound(): LabelValueUnit {
		if (!this.#sound) {
			this.#sound = new LabelValueUnit();
			this.#sound.minWidth = 184;
			this.#sound.width = 'fill';
			this.#sound.text = 'Lyddata';
			this.#sound.unit = 'RW';
		}
		return this.#sound;
	}

	/** What the model costs to make. */
	public get costPrice(): LabelValueUnit {
		if (!this.#costPrice) {
			this.#costPrice = new LabelValueUnit();
			this.#costPrice.minWidth = 184;
			this.#costPrice.width = 'fill';
			this.#costPrice.text = 'Kostpris';
			this.#costPrice.unit = 'Kr.';
		}
		return this.#costPrice;
	}

	/** What the model is sold for. */
	public get listPrice(): LabelValueUnit {
		if (!this.#listPrice) {
			this.#listPrice = new LabelValueUnit();
			this.#listPrice.minWidth = 184;
			this.#listPrice.width = 'fill';
			this.#listPrice.text = 'Listepris';
			this.#listPrice.unit = 'Kr.';
		}
		return this.#listPrice;
	}

	/** What the elements of the model cost to make, of the cost price above. */
	public get costPriceElements(): LabelValueUnit {
		if (!this.#costPriceElements) {
			this.#costPriceElements = new LabelValueUnit();
			this.#costPriceElements.minWidth = 184;
			this.#costPriceElements.width = 'fill';
			this.#costPriceElements.text = 'Kostpris Elementer';
			this.#costPriceElements.unit = 'Kr.';
		}
		return this.#costPriceElements;
	}

	/** What the elements of the model are sold for, of the list price above. */
	public get listPriceElements(): LabelValueUnit {
		if (!this.#listPriceElements) {
			this.#listPriceElements = new LabelValueUnit();
			this.#listPriceElements.minWidth = 184;
			this.#listPriceElements.width = 'fill';
			this.#listPriceElements.text = 'Listepris Elementer';
			this.#listPriceElements.unit = 'Kr.';
		}
		return this.#listPriceElements;
	}

	/** What the glass in the model costs to make, of the cost price above. */
	public get costPriceGlasses(): LabelValueUnit {
		if (!this.#costPriceGlasses) {
			this.#costPriceGlasses = new LabelValueUnit();
			this.#costPriceGlasses.minWidth = 184;
			this.#costPriceGlasses.width = 'fill';
			this.#costPriceGlasses.text = 'Kostpris Glas';
			this.#costPriceGlasses.unit = 'Kr.';
		}
		return this.#costPriceGlasses;
	}

	/** What the glass in the model is sold for, of the list price above. */
	public get listPriceGlasses(): LabelValueUnit {
		if (!this.#listPriceGlasses) {
			this.#listPriceGlasses = new LabelValueUnit();
			this.#listPriceGlasses.minWidth = 184;
			this.#listPriceGlasses.width = 'fill';
			this.#listPriceGlasses.text = 'Listepris Glas';
			this.#listPriceGlasses.unit = 'Kr.';
		}
		return this.#listPriceGlasses;
	}

	/** What the coating on the model costs to make, of the cost price above. */
	public get costPriceCoating(): LabelValueUnit {
		if (!this.#costPriceCoating) {
			this.#costPriceCoating = new LabelValueUnit();
			this.#costPriceCoating.minWidth = 184;
			this.#costPriceCoating.width = 'fill';
			this.#costPriceCoating.text = 'Kostpris Overflade';
			this.#costPriceCoating.unit = 'Kr.';
		}
		return this.#costPriceCoating;
	}

	/** What the coating on the model is sold for, of the list price above. */
	public get listPriceCoating(): LabelValueUnit {
		if (!this.#listPriceCoating) {
			this.#listPriceCoating = new LabelValueUnit();
			this.#listPriceCoating.minWidth = 184;
			this.#listPriceCoating.width = 'fill';
			this.#listPriceCoating.text = 'Listepris Overflade';
			this.#listPriceCoating.unit = 'Kr.';
		}
		return this.#listPriceCoating;
	}

	/**
	 * What went wrong the last time the position was sent to the engine, under the properties.
	 *
	 * Beside `content` rather than in it, and that is the point: the properties hide as one when the engine has worked nothing out — see #commitProperties — and a position whose configuring or first pricing failed has a refusal to show and no properties. Out here the card is shown whatever the properties are doing,
	 * and sits under them when there are any, the way a note comes after the figures it is about. It reads the model and shows itself, so the panel adds it and does nothing more — see `PositionErrorMessage`.
	 */
	public get positionErrorMessage(): PositionErrorMessage {
		if (!this.#positionErrorMessage) {
			this.#positionErrorMessage = new PositionErrorMessage();
		}
		return this.#positionErrorMessage;
	}

	protected override initialize(): void {
		super.initialize();
		this.body.addComponents([this.content, this.positionErrorMessage, this.spinner]);
		this.addComponents([this.header, this.body]);
		this.propertiesChanged(ConfiguratorModel.currentProperties.value);
		this.shownChanged(ConfiguratorModel.propertiesPanelShown.value);
		// Subscribed here rather than in the constructor, and only once the card has been made: the
		// card sets its own visibility from the error, and listeners are told in the order they
		// subscribed, so the one that subscribes last has the last word. The panel has to have it, to
		// keep the card down while the spinner is up — see waitingChanged. What the model already
		// holds is read straight after, since a subscription only hears what changes from here on.
		ConfiguratorModel.currentProperties.subscribe(this.waitingChanged);
		ConfiguratorModel.currentError.subscribe(this.waitingChanged);
		ConfiguratorModel.configurationInProgress.subscribe(this.waitingChanged);
		ConfiguratorModel.calculationInProgress.subscribe(this.waitingChanged);
		this.waitingChanged();
	}

	/**
	 * Unsubscribes, because the model outlives the panel.
	 *
	 * `ConfiguratorModel` is a module constant, so a listener it still holds is a panel that has been removed and cannot be collected.
	 */
	protected override disconnected(): void {
		super.disconnected();
		ConfiguratorModel.currentProperties.unsubscribe(this.propertiesChanged);
		ConfiguratorModel.propertiesPanelShown.unsubscribe(this.shownChanged);
		ConfiguratorModel.currentProperties.unsubscribe(this.waitingChanged);
		ConfiguratorModel.currentError.unsubscribe(this.waitingChanged);
		ConfiguratorModel.configurationInProgress.unsubscribe(this.waitingChanged);
		ConfiguratorModel.calculationInProgress.unsubscribe(this.waitingChanged);
	}

	/**
	 * Gives the room under the header to the spinner while a price is on its way and none is on screen, and back to the properties and the error card otherwise.
	 *
	 * The same rule as the drawing section's and the geometry dialog's, read off the properties in place of the drawing: a call out for the selected position and nothing to show meanwhile is waiting, and a spinner says so where the properties will be. Waiting comes first: an error still on the position while the next
	 * answer is on its way is about the answer before, so the card is kept down until the new one has landed, refused or not. The card sets its own visibility from the error, which is why the panel is told after it — see the subscriptions in initialize. The properties need no telling: they are hidden whenever there
	 * are none, which is the only time the spinner is up.
	 */
	private waitingChanged(): void {
		const waiting = (ConfiguratorModel.configurationInProgress.value || ConfiguratorModel.calculationInProgress.value) && ConfiguratorModel.currentProperties.value === null;
		this.spinner.visible = waiting;
		this.spinner.spin = waiting;
		this.positionErrorMessage.visible = !waiting && ConfiguratorModel.currentError.value !== null;
	}

	/** Shows whatever the model now holds, which is nothing when it holds nothing. */
	private propertiesChanged(properties: IBom | null): void {
		this.properties = properties;
	}

	/**
	 * Shows or hides the whole panel, as the model says.
	 *
	 * Hidden is out of the layout, so the sections beside it take its width; shown, it takes its 400 back. The properties go on being written while it is hidden — see the subscription in the constructor — so showing it again shows the position the user is on, not the one they left.
	 */
	private shownChanged(shown: boolean): void {
		this.visible = shown;
	}

	protected override commitProperties(): void {
		super.commitProperties();
		if (this.#propertiesChanged) {
			this.#commitProperties();
		}
	}

	/**
	 * The fields are hidden rather than emptied, so nothing on screen claims the engine worked out nothing.
	 *
	 * Which also means there is nothing to write when there is nothing to show: the fields keep what they last held, out of sight, and are written over the moment there is something to write. The three costs the engine reports in halves are read off the BOM's totals — see `Bom`.
	 */
	#commitProperties(): void {
		this.#propertiesChanged = false;
		const properties = this.properties;
		this.content.visible = properties !== null;
		if (properties === null) {
			return;
		}
		this.modelWeight.value = properties.weight;
		this.glassWeight.value = properties.glassWeight;
		this.uValue.value = properties.uValue;
		this.eref.value = properties.eref;
		this.sunlightTransmittance.value = properties.sunlightTransmittance;
		this.sound.value = properties.soundRW;
		this.costPrice.value = properties.costPrice;
		this.listPrice.value = properties.listPrice;
		this.costPriceElements.value = properties.costPriceElementsTotal;
		this.listPriceElements.value = properties.listPriceElements;
		this.costPriceGlasses.value = properties.costPriceGlassesTotal;
		this.listPriceGlasses.value = properties.listPriceGlasses;
		this.costPriceCoating.value = properties.costPriceCoatingTotal;
		this.listPriceCoating.value = properties.listPriceCoating;
	}
}
customElements.define('ac-properties-panel', PropertiesPanel);
